import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  CalAm,
  CalAmBulk,
  getInitFormData,
  setConfigToColumns,
  setUmByFgUm,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import SnComboBox from "@shared/components/SnComboBox";
import SnLabel from "@shared/components/SnLabel";
import { RowNode } from "ag-grid-community";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}

const SN_PUR_APP_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  const [valueTpUm, setValueTpUm] = useState<string>("001");
  let isUpdating = false;
  const [selectedDocu, setSelectedDocu] = useState<string>(""); // file 관련

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData({});
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        const configGrid1 = setConfigToColumns(rtn.grid.grid2, "MAS0002");
        const configGrid2 = setConfigToColumns(configGrid1, "PUR0105");
        setColums({
          grid1: rtn.grid.grid1,
          grid2: configGrid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_pur_app_reg_002_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onSave = async (msg?: string) => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();

      const purAppHChanges = gridRefs.current["grid1"].current.getChange();
      const purAppLChanges = gridRefs.current["grid2"].current.getChange();

      if (
        purAppHChanges.modifiedRowcount === 0 &&
        purAppLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const saveData = {
        pur_apph: purAppHChanges,
        pur_appl: purAppLChanges,
      };

      const ok = await fnSave("SN_PUR_APP_REG_002_SAVE_01", saveData, msg);
      if (!ok) return;

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
      return false;
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      // 배열로 만들어서 필터링 함수 호출
      const no_app = selectedRow.no_app;
      setSelectedDocu(no_app);
    } else {
      return;
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    let msg = "";
    const rowCount = gridRefs.current["grid1"].current.getDisplayedRowCount();
    if (rowCount === 0) return;

    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    try {
      if (id === "btnApprove") {
        if (focusedNodeH.data.st_app === "R") {
          await showDialog("alter", "이미 승인된 건입니다.");
          return;
        }
        gridRefs.current["grid1"].current.setValueImmediately(
          focusedNodeH,
          "st_app",
          "R"
        );
        msg = "APPROVE";
      }
      if (id === "btnCancelAprove") {
        if (focusedNodeH.data.st_app === "F") {
          await showDialog("alter", "이미 미승인 건입니다.");
          return;
        }
        gridRefs.current["grid1"].current.setValueImmediately(
          focusedNodeH,
          "st_app",
          "F"
        );
        msg = "APPROVE_CANCEL";
      }
      if (id === "btnClosing") {
        if (focusedNodeH.data.st_app === "C") {
          await showDialog("alter", "이미 마감된 건입니다.");
          return;
        }
        gridRefs.current["grid1"].current.setValueImmediately(
          focusedNodeH,
          "st_app",
          "C"
        );
        msg = "CLOSE";
      }
      if (id === "btnCancelClosing") {
        if (focusedNodeH.data.st_app === "F") {
          await showDialog("alter", "이미 미승인 건입니다.");
          return;
        }
        gridRefs.current["grid1"].current.setValueImmediately(
          focusedNodeH,
          "st_app",
          "F"
        );
        msg = "CLOSE_CANCEL";
      }
      onSave(msg);
    } catch (error) {}
  };

  const onGrid2BtnsClick = async (id: string) => {
    const rowCount = gridRefs.current["grid2"].current.getDisplayedRowCount();
    if (rowCount === 0) return;

    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const selectedNoApp = focusedNodeH?.data?.no_app;
    if (!selectedNoApp) return;

    try {
      if (id === "btnChangeUm") {
        const rowCount =
          gridRefs.current["grid2"].current.getDisplayedRowCount();
        if (rowCount === 0) return;

        try {
          const focusedNodeH =
            gridRefs.current["grid1"].current.focusedRowNode();
          if (focusedNodeH) {
            if (focusedNodeH.data?.st_app === "R") {
              await showDialog(
                "alter",
                "승인된 건 입니다.\r\n승인취소 후 진행해주시기 바랍니다."
              );
              return;
            }
          }

          let displayedNodes: any[] = [];
          gridRefs.current["grid2"].current.forEachFilteredNode(
            (node: RowNode) => displayedNodes.push(node)
          );

          await setUmByFgUm(
            gridRefs.current["grid1"],
            gridRefs.current["grid2"],
            "dt_app",
            valueTpUm,
            displayedNodes
          );

          displayedNodes.forEach((node: RowNode) => {
            node.setDataValue("tp_um", valueTpUm);
          });
        } catch (error) {
          console.error(error);
        }
      }
      if (id == "btnApplyRecentUm") {
        const displayedNodes: any[] = [];
        gridRefs.current["grid2"].current.forEachFilteredNode(
          (node: RowNode) => {
            displayedNodes.push(node);
          }
        );

        for (const node of displayedNodes) {
          if (!node?.data) continue;
          if (node.data.no_app !== selectedNoApp) continue;
          const params = {
            id: "sn_pur_app_reg_002_searchum",
            params: {
              cd_firm: userInfo?.cd_firm ?? "",
              cd_item: node.data?.cd_item ?? "",
              cd_cust: node.data?.cd_cust ?? "",
              cd_exch: node.data?.cd_exch ?? "",
            },
          };
          const um = await callApi("SN_PUR_APP_REG_002_getUm", params);
          if (um?.data) {
            const umValue = um?.data;
            if (umValue !== undefined && umValue !== 0) {
              node.setDataValue("um", umValue);
            }
          }
          await CalAmBulk(
            gridRefs.current["grid1"],
            gridRefs.current["grid2"],
            "rt_exch"
          );
        }
      }
    } catch (error) {}
  };

  const onCellValueChanged = async (event: CellValueChangedEvent) => {
    if (!event) return;
    if (isUpdating) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    if (colId === "qt" || colId === "um" || colId === "am") {
      await CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
    }

    if (colId === "tp_um" || colId === "cd_exch") {
      await setUmByFgUm(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "dt_app",
        event.rowData.tp_um
      );
    }
  };

  const onValueChanged = (value: string) => {
    setValueTpUm(value);
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChanged,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChanged,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "구매정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_app"] }}
            header={
              <div className="flex space-x-2" style={{ padding: 5 }}>
                <SnButton
                  text={"승인"}
                  onClick={() => {
                    onGrid1BtnsClick("btnApprove");
                  }}
                />
                <SnButton
                  text={"승인취소"}
                  onClick={() => {
                    onGrid1BtnsClick("btnCancelAprove");
                  }}
                />
                <SnButton
                  text={"마감"}
                  onClick={() => {
                    onGrid1BtnsClick("btnClosing");
                  }}
                />
                <SnButton
                  text={"마감취소"}
                  onClick={() => {
                    onGrid1BtnsClick("btnCancelClosing");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "multiple", title: "상세정보" }}
            events={grids.grid2.events}
            header={
              <div className="relative flex space-x-1">
                <SnLabel text="단가유형" type="string" />
                <div style={{ width: 200 }}>
                  <SnComboBox
                    defaultValue={"001"}
                    code="PUR001"
                    sql=""
                    isEditable={true}
                    onValueChanged={onValueChanged}
                  />
                </div>
                <div className="relative flex">
                  <div style={{ width: 120 }}>
                    <SnButton
                      text={"단가일괄변경"}
                      onClick={() => onGrid2BtnsClick("btnChangeUm")}
                    />
                  </div>
                  <div style={{ width: 120 }}>
                    <SnButton
                      text={"최근단가적용"}
                      onClick={() => onGrid2BtnsClick("btnApplyRecentUm")}
                    />
                  </div>
                </div>
              </div>
            }
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_PUR_APP_REG_002;
